import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, SearchX, ChevronDown } from "lucide-react";
import { api, getCategories } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ProductCard } from "@/components/store/ProductCard";
import { Seo } from "@/components/Seo";

// textos curados das categorias reais da loja (editáveis no painel, em Admin → SEO)
const CATEGORY_SEO = {
  "sofas": [
    "Sofás Retráteis, Reclináveis e de Canto | HG Móveis",
    "Sofás retráteis, reclináveis e de canto com ótimos preços. Compare cores, medidas e modelos na HG Móveis. Pagamento na entrega em regiões do RJ.",
  ],
  "guarda-roupas": [
    "Guarda-Roupas de Casal e Solteiro | HG Móveis",
    "Confira guarda-roupas de casal e solteiro na HG Móveis. Modelos com espelho, gavetas e diferentes tamanhos. Consulte as regiões atendidas no RJ.",
  ],
  "camas": [
    "Camas de Casal e Solteiro | HG Móveis",
    "Camas de casal e solteiro com qualidade e ótimo preço na HG Móveis. Pagamento somente na entrega em regiões atendidas do RJ.",
  ],
  "mesas": [
    "Mesas de Jantar | HG Móveis",
    "Mesas de jantar para sala e cozinha com pagamento na entrega. Veja modelos, tamanhos e cadeiras na HG Móveis.",
  ],
  "racks-e-paineis": [
    "Racks e Painéis para TV | HG Móveis",
    "Racks e painéis para TV para deixar sua sala organizada e bonita. Pagamento na entrega em regiões do RJ. Confira na HG Móveis.",
  ],
  "cozinha": [
    "Cozinhas e Armários de Cozinha | HG Móveis",
    "Cozinhas completas e armários de cozinha com ótimos preços na HG Móveis. Pagamento somente na entrega no RJ.",
  ],
};
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function CatalogPage() {
  const settings = useSettings();
  const filtersEnabled = (settings.features || {}).filters !== false;
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const busca = searchParams.get("busca") || "";
  const promo = searchParams.get("promo") === "1";

  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [meta, setMeta] = useState({ colors: [], min_price: 0, max_price: 0 });
  const [categories, setCategories] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [selectedColors, setSelectedColors] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [inStock, setInStock] = useState(false);
  const [sort, setSort] = useState("relevancia");

  const hasFilters = selectedColors.length > 0 || minPrice || maxPrice || inStock;
  const clearFilters = () => {
    setSelectedColors([]);
    setMinPrice("");
    setMaxPrice("");
    setInStock(false);
  };

  useEffect(() => {
    setPage(1);
    clearFilters();
  }, [slug, busca, promo]);

  useEffect(() => {
    getCategories().then((cats) => { setCategories(cats.filter((c) => !c.parent_id)); setAllCats(cats); }).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/catalog/meta", { params: { category: slug || undefined } })
      .then((r) => setMeta(r.data))
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const params = {
      search: busca || undefined,
      category: slug || undefined,
      promo: promo || undefined,
      sort,
      page,
      limit: 12,
    };
    if (minPrice) params.min_price = Number(minPrice);
    if (maxPrice) params.max_price = Number(maxPrice);
    if (inStock) params.in_stock = true;

    const fetchAll = selectedColors.length === 0
      ? api.get("/products", { params })
      : Promise.all(
          selectedColors.map((c) => api.get("/products", { params: { ...params, color: c } }))
        ).then((results) => {
          const map = new Map();
          results.forEach((r) => r.data.items.forEach((p) => map.set(p.id, p)));
          const items = [...map.values()];
          return { data: { items, total: items.length, pages: 1 } };
        });

    fetchAll
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, busca, promo, sort, page, minPrice, maxPrice, selectedColors, inStock]);

  const title = useMemo(() => {
    if (busca) return `Resultados para "${busca}"`;
    if (promo) return "Ofertas";
    if (slug) {
      const cat = categories.find((c) => c.slug === slug) || allCats.find((c) => c.slug === slug);
      return cat ? cat.name : "Produtos";
    }
    return "Todos os Produtos";
  }, [busca, promo, slug, categories, allCats]);

  // SEO por categoria: campos editáveis no painel > textos curados por categoria > geração automática
  const seoInfo = useMemo(() => {
    if (busca) return null;
    if (promo) return {
      title: "Ofertas de Móveis | HG Móveis",
      description: "Móveis com preços especiais: sofás, guarda-roupas, camas e cozinhas. Pagamento somente na entrega em regiões do RJ.",
    };
    if (slug) {
      const cat = allCats.find((c) => c.slug === slug);
      if (!cat) return null;
      const curated = CATEGORY_SEO[slug];
      const parent = allCats.find((c) => c.id === cat.parent_id);
      return {
        title: cat.seo_title || (curated ? curated[0] : `${cat.name}${parent ? ` — ${parent.name}` : ""} | HG Móveis`),
        description: cat.seo_description || (curated ? curated[1] : `${cat.name} com ótimos preços na HG Móveis. Compare modelos, cores e medidas. Pagamento somente na entrega em regiões atendidas do RJ.`),
      };
    }
    return {
      title: "Todos os Produtos | HG Móveis",
      description: "Catálogo completo da HG Móveis: sofás, guarda-roupas, camas, mesas, racks, painéis para TV e cozinhas. Pagamento na entrega no RJ.",
    };
  }, [busca, promo, slug, allCats]);

  const toggleColor = (color) =>
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );

  // seções recolhíveis do painel de filtros
  const FilterSection = ({ id, title, children, defaultOpen = true }) => (
    <details open={defaultOpen} className="group border-b border-gray-100 last:border-0 pb-4 last:pb-0" data-testid={`filter-section-${id}`}>
      <summary className="flex items-center justify-between cursor-pointer list-none py-1 font-heading font-semibold text-gray-900 text-sm select-none">
        {title}
        <ChevronDown className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );

  const Filters = () => (
    <div className="space-y-4" data-testid="catalog-filters">
      <FilterSection id="categorias" title="Categorias">
        <ul className="space-y-1">
          <li>
            <Link
              to="/produtos"
              className={`block text-sm py-1 ${!slug ? "font-semibold text-brand" : "text-gray-600 hover:text-brand"}`}
              data-testid="filter-cat-all"
            >
              Todas
            </Link>
          </li>
          {categories.map((cat) => {
            const subs = allCats.filter((c) => c.parent_id === cat.id);
            return (
              <li key={cat.id}>
                <Link
                  to={`/categoria/${cat.slug}`}
                  className={`block text-sm py-1 ${slug === cat.slug ? "font-semibold text-brand" : "text-gray-600 hover:text-brand"}`}
                  data-testid={`filter-cat-${cat.slug}`}
                >
                  {cat.name}
                </Link>
                {subs.length > 0 && (
                  <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-gray-100 pl-3">
                    {subs.map((sub) => (
                      <li key={sub.id}>
                        <Link
                          to={`/categoria/${sub.slug}`}
                          className={`block text-[13px] py-0.5 ${slug === sub.slug ? "font-semibold text-brand" : "text-gray-500 hover:text-brand"}`}
                          data-testid={`filter-subcat-${sub.slug}`}
                        >
                          {sub.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </FilterSection>

      <FilterSection id="preco" title="Faixa de preço">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="Mín"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            className="h-9"
            data-testid="filter-price-min"
          />
          <span className="text-gray-400">—</span>
          <Input
            type="number"
            placeholder="Máx"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            className="h-9"
            data-testid="filter-price-max"
          />
        </div>
        {meta.max_price > 0 && (
          <p className="text-xs text-gray-400 mt-1.5">
            De R$ {meta.min_price.toFixed(0)} a R$ {meta.max_price.toFixed(0)}
          </p>
        )}
      </FilterSection>

      {meta.colors.length > 0 && (
        <FilterSection id="cores" title="Cores">
          <div className="flex flex-wrap gap-1.5">
            {meta.colors.map((color) => {
              const active = selectedColors.includes(color);
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => toggleColor(color)}
                  aria-pressed={active}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border transition-colors ${
                    active
                      ? "bg-brand text-white border-brand"
                      : "bg-white text-gray-600 border-gray-200 hover:border-brand hover:text-brand"
                  }`}
                  data-testid={`filter-color-${color.toLowerCase()}`}
                >
                  {color}
                </button>
              );
            })}
          </div>
        </FilterSection>
      )}

      <FilterSection id="disponibilidade" title="Disponibilidade">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <Checkbox checked={inStock} onCheckedChange={(v) => setInStock(!!v)} data-testid="filter-in-stock" />
          Somente em estoque
        </label>
      </FilterSection>

      {hasFilters && (
        <Button variant="outline" size="sm" className="w-full rounded-full" onClick={clearFilters} data-testid="filter-clear">
          Limpar todos os filtros
        </Button>
      )}
    </div>
  );

  return (
    <div className="container-store py-8" data-testid="catalog-page">
      <Seo
        title={seoInfo ? seoInfo.title : `${title} | HG Móveis`}
        description={seoInfo ? seoInfo.description : `Confira ${title.toLowerCase()} na HG Móveis: compare preços, cores e medidas, com pagamento na entrega.`}
        canonical={slug ? `/categoria/${slug}` : "/produtos"}
        noindex={!!busca}
      />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="catalog-title">
            {title}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-testid="catalog-total">
            {data.total} produto(s) encontrado(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {filtersEnabled && (
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden rounded-full" data-testid="mobile-filters-button">
                <SlidersHorizontal className="w-4 h-4" /> Filtros
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Filtros</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <Filters />
              </div>
            </SheetContent>
          </Sheet>
          )}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[170px] rounded-full" data-testid="sort-select">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevancia">Mais relevantes</SelectItem>
              <SelectItem value="mais-vendidos">Mais vendidos</SelectItem>
              <SelectItem value="menor-preco">Menor preço</SelectItem>
              <SelectItem value="maior-preco">Maior preço</SelectItem>
              <SelectItem value="novidades">Novidades</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={`mt-8 grid grid-cols-1 ${filtersEnabled ? "lg:grid-cols-[240px_1fr]" : ""} gap-8`}>
        {filtersEnabled && (
        <aside className="hidden lg:block">
          <div className="bg-white border border-gray-100 rounded-xl p-5 sticky top-40">
            <Filters />
          </div>
        </aside>
        )}

        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-gray-100" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                    <div className="h-5 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-12 text-center" data-testid="catalog-empty">
              <SearchX className="w-12 h-12 text-gray-300 mx-auto" />
              <h3 className="mt-4 font-heading font-semibold text-lg text-gray-800">
                Nenhum produto encontrado
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Tente ajustar os filtros ou fale conosco pelo WhatsApp.
              </p>
            </div>
          ) : (
            <>
              <div className={`grid grid-cols-2 gap-4 md:gap-6 ${filtersEnabled ? "md:grid-cols-3" : "sm:grid-cols-3 lg:grid-cols-4"}`} data-testid="catalog-grid">
                {data.items.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
              {data.pages > page && (
                <div className="mt-8 text-center">
                  <Button
                    variant="outline"
                    className="rounded-full px-8"
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="load-more-button"
                  >
                    Carregar mais produtos
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
